import { Fragment } from 'react';
import { Check, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  onBoostPass: (tier: 'premium_economy' | 'first_class', duration: string, price: number) => void;
  onStartFree: () => void;
  onChoosePlan: (planId: string) => void;
}

const COLUMNS = [
  { key: 'free', name: 'Economy', price: 'Free', note: 'Occasional trips' },
  { key: 'boost', name: 'Boost Pass', price: 'from €2.99', note: '3, 7 or 14 days' },
  { key: 'premium', name: 'Premium Economy', price: '€6.99/mo', note: 'Most trips' },
  { key: 'first', name: 'First Class', price: '€11.99/mo', note: 'Multi-city, frequent' },
] as const;

type Cell = boolean | string;

interface Row {
  label: string;
  short?: string;
  mv?: Cell;
  free: Cell;
  boost: Cell;
  premium: Cell;
  first: Cell;
}

const GROUPS: { title: string; rows: Row[] }[] = [
  {
    title: 'Everyday tools',
    rows: [
      { label: 'AI packing lists', short: 'AI packing', free: '3 a day', boost: 'Unlimited', premium: 'Unlimited', first: 'Unlimited' },
      { label: 'Visa & entry checks', short: 'Visa checks', free: '3 a day', boost: 'Unlimited', premium: 'Unlimited', first: 'Unlimited' },
      { label: 'Jet lag minimizer', short: 'Jet lag', free: '3 a day', boost: 'Unlimited', premium: 'Unlimited', first: 'Unlimited' },
    ],
  },
  {
    title: 'Everything premium',
    rows: [
      { label: 'Weather & health prep', short: 'Weather & health', free: false, boost: true, premium: true, first: true },
      { label: 'Local destination tips', short: 'Local tips', free: false, boost: true, premium: true, first: true },
      { label: 'Currency converter', short: 'Currency', free: false, boost: true, premium: true, first: true },
      { label: 'Group expense sharing', short: 'Expenses', free: false, boost: true, premium: true, first: true },
    ],
  },
  {
    title: 'First Class only',
    rows: [
      { label: 'Offline access', short: 'Offline', free: false, boost: false, premium: false, first: true },
      { label: 'Multi-city packing lists', short: 'Multi-city', free: false, boost: false, premium: false, first: true },
      { label: 'Saved templates & analytics', short: 'Templates', free: false, boost: false, premium: false, first: true },
    ],
  },
];

const DURATION: Row = {
  label: 'How long it lasts',
  short: 'Lasts',
  mv: undefined,
  free: 'Forever',
  boost: '3/7/14 days',
  premium: 'Till cancel',
  first: 'Till cancel',
};

const CellValue = ({ value, emphasis }: { value: Cell; emphasis?: boolean }) => {
  if (value === true)
    return <Check className={`mx-auto h-4 w-4 ${emphasis ? 'text-primary' : 'text-primary/80'}`} aria-label="Included" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" aria-label="Not included" />;
  return <span className={`text-sm ${emphasis ? 'font-semibold text-primary' : 'text-foreground'}`}>{value}</span>;
};

export const PricingComparison = ({ onBoostPass, onStartFree, onChoosePlan }: Props) => (
  <section id="compare-plans" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-24" aria-labelledby="comparison-heading">
    <div className="max-w-7xl mx-auto">
      <div className="mb-10 sm:mb-14">
        <h2 id="comparison-heading" className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-foreground">
          Your options, side by side.
        </h2>
        <p className="text-base sm:text-xl text-muted-foreground max-w-3xl leading-relaxed">
          Same trip-planning engine in all four. The difference is how much you use it and for how long.
        </p>
      </div>

      {/* Desktop / tablet: compact grid */}
      <div className="hidden lg:block border-y border-border">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">
            What each OdyVoy option includes: Economy free plan, Boost Pass, Premium Economy and First Class.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="px-5 pt-6 pb-3 text-sm uppercase text-muted-foreground align-bottom">
                Capability
              </th>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`px-3 pt-6 pb-3 text-center align-bottom ${
                    c.key === 'boost' ? 'border-t-2 border-primary bg-primary/10' : ''
                  }`}
                >
                  <span className={`block text-base font-bold text-foreground`}>{c.name}</span>
                  <span className="block text-sm font-semibold text-primary">{c.price}</span>
                  <span className="block text-sm font-medium text-muted-foreground">{c.note}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((group) => (
              <Fragment key={group.title}>
                <tr className="bg-muted/50">
                  <td colSpan={5} className="px-5 py-3 text-sm font-medium uppercase text-foreground">
                    {group.title}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label} className="border-b border-border/40">
                    <th scope="row" className="px-5 py-5 text-sm font-normal text-foreground">
                      {row.label}
                    </th>
                    <td className="px-3 py-5 text-center"><CellValue value={row.free} /></td>
                    <td className="px-3 py-5 text-center bg-primary/10"><CellValue value={row.boost} emphasis /></td>
                    <td className="px-3 py-5 text-center"><CellValue value={row.premium} /></td>
                    <td className="px-3 py-5 text-center"><CellValue value={row.first} /></td>
                  </tr>
                ))}
              </Fragment>
            ))}
            <tr className="border-t border-border bg-muted/25">
              <th scope="row" className="px-5 py-5 text-sm font-semibold text-foreground">
                {DURATION.label}
              </th>
              <td className="px-3 py-5 text-center"><CellValue value={DURATION.free} /></td>
              <td className="px-3 py-5 text-center bg-primary/10"><CellValue value={DURATION.boost} emphasis /></td>
              <td className="px-3 py-5 text-center"><CellValue value={DURATION.premium} /></td>
              <td className="px-3 py-5 text-center"><CellValue value={DURATION.first} /></td>
            </tr>
            <tr>
              <td className="px-5 py-3" />
              <td className="px-3 py-3 text-center">
                <Button variant="outline" size="sm" className="h-14 px-4 text-sm rounded" onClick={onStartFree}>
                  Start free
                </Button>
              </td>
              <td className="px-3 py-3 text-center bg-primary/10">
                <Button size="sm" className="h-14 px-4 text-sm rounded" onClick={() => onBoostPass('premium_economy', '3-Day', 2.99)}>
                  Grab a 3-Day Pass
                </Button>
              </td>
              <td className="px-3 py-3 text-center">
                <Button variant="outline" size="sm" className="h-14 px-4 text-sm rounded" onClick={() => onChoosePlan('premium_economy')}>
                  Get Premium Economy
                </Button>
              </td>
              <td className="px-3 py-3 text-center">
                <Button variant="outline" size="sm" className="h-14 px-4 text-sm rounded" onClick={() => onChoosePlan('first_class')}>
                  Get First Class
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile: compact stacked cards, no horizontal scroll */}
      <div className="lg:hidden space-y-2.5">
        {COLUMNS.map((c) => (
          <div key={c.key} className="rounded-xl border border-border/60 bg-card p-3">
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className={`text-sm font-bold text-foreground`}>{c.name}</span>
              <span className="text-xs font-semibold text-primary">{c.price}</span>
            </div>
            <ul className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-x-4 gap-y-2">
              {[...GROUPS.flatMap((g) => g.rows), DURATION].map((row) => {
                const value = row.mv ?? row[c.key];
                const isDuration = row === DURATION;
                return (
                  <li
                    key={row.label}
                    className={`flex items-baseline justify-between gap-3 text-xs min-w-0 ${
                      isDuration ? 'min-[400px]:col-span-2 border-t border-border/50 mt-1 pt-1' : ''
                    }`}
                  >
                    <span className={`min-w-0 whitespace-normal ${isDuration ? 'font-semibold text-foreground' : 'text-foreground/75'}`}>
                      {row.short ?? row.label}
                    </span>
                    <span className="shrink-0 font-medium text-foreground">
                      {value === true ? 'Yes' : value === false ? '—' : value}
                    </span>
                  </li>
                );
              })}
            </ul>
            {c.key === 'boost' && (
              <Button className="mt-2.5 h-12 w-full text-sm" onClick={() => onBoostPass('premium_economy', '3-Day', 2.99)}>
                Grab a 3-Day Pass — €2.99
              </Button>
            )}
            {c.key === 'free' && (
              <Button variant="outline" className="mt-2.5 h-12 w-full text-sm" onClick={onStartFree}>
                Start free
              </Button>
            )}
            {(c.key === 'premium' || c.key === 'first') && (
              <Button variant="outline" className="mt-2.5 h-12 w-full text-sm" onClick={() => onChoosePlan(c.key === 'premium' ? 'premium_economy' : 'first_class')}>
                Get {c.name}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);
